import React, { useEffect, useCallback, useState, ReactNode } from "react";
import useLocalStorage from "@/hooks/useLocalStorage";
import { useUser } from "@/context/UserContext";
import OneSignal from "react-onesignal";
import config from "@/config";
import { getUserGuessesPerPeriod } from "@/services/UserService";
import { useAuth0 } from "@auth0/auth0-react";
import { useQuery } from "@tanstack/react-query";
import { getUserNotificationSettings } from "@/services/NotificationService";

interface OneSignalWrapperProps {
    children: ReactNode;
}

const OneSignalWrapper: React.FC<OneSignalWrapperProps> = ({ children }) => {
    const { isAuthenticated } = useAuth0();
    const [value] = useLocalStorage("guest_gameplay", []); // eslint-disable-line no-unused-vars
    const [userData] = useLocalStorage("user_data", {});
    const { userState } = useUser();
    const [isInitializedOneSignal, setIsInitializedOneSignal] = useState<boolean>(false);

    // Get users notification settings
    const userNotifSettings = useQuery({
        queryKey: ["user-notif-settings", userState?.id],
        queryFn: () => getUserNotificationSettings(userState?.id),
        enabled: !!userState?.id && isAuthenticated,
    });

    /**
     * Function that sets the frequency tag of a user
     */
    const notifMessage = userNotifSettings?.data?.message as any;
    const setPushFrequency = useCallback(async () => {
        try {
            // Don't execute if user is not logged in
            if (!isAuthenticated) return;

            const pushFrequency = notifMessage?.push_frequency;
            if (pushFrequency === "daily") {
                OneSignal.User.addTag("frequency", "daily");
            } else if (pushFrequency === "weekly") {
                OneSignal.User.addTag("frequency", "weekly");
            } else {
                OneSignal.User.addTag("frequency", "never");
            }
        } catch (error) {
            console.log(error);
        }
    }, [isAuthenticated, notifMessage]);

    /**
     * Function to initialize OneSignal
     */
    const initOneSignal = useCallback(async () => {
        // Check if OneSignal is already initialized
        if (isInitializedOneSignal) {
            return;
        }

        // Initialize OneSignal
        await OneSignal.init({
            appId: config.ONESIGNAL_ID,
            allowLocalhostAsSecureOrigin: true,
            promptOptions: {
                customlink: {
                    enabled: true /* Required to use the Custom Link */,
                    style: "button" /* Has value of 'button' or 'link' */,
                    size: "medium" /* One of 'small', 'medium', or 'large' */,
                    color: {
                        button: "#E12D30" /* Color of the button background if style = "button" */,
                        text: "#FFFFFF" /* Color of the prompt's text */,
                    },
                    text: {
                        subscribe: "Subscribe" /* Prompt's text when not subscribed */,
                        unsubscribe: "Unsubscribe" /* Prompt's text when subscribed */,
                        explanation:
                            "Almost done! Get notified when the next batch of properties is ready." /* Optional text appearing before the prompt button */,
                    },
                    unsubscribeEnabled: true /* Controls whether the prompt is visible after subscription */,
                },
            } as any,
        });

        setIsInitializedOneSignal(true);
    }, [isInitializedOneSignal]);

    // Sets has played tag for OneSignal
    const hasUserCreatedAt = Boolean((userData as any)?.created_at);
    const guestPlaysLength = ((value as any[])?.length ?? 0) as number;
    const setHasPlayed = useCallback(async () => {
        try {
            // Check if OneSignal is initialized
            if (isInitializedOneSignal) {
                // If isAuthenticated count user guesses from API, else get from local storage
                if (hasUserCreatedAt) {
                    if (userState?.id) {
                        const userGuesses = await getUserGuessesPerPeriod({
                            userId: userState?.id as string,
                            gameVersion: "classic",
                        });
                        // hasPlayed tag setter
                        if (userGuesses?.data?.count?.total > 0) {
                            OneSignal.User.addTag("hasPlayed", "true");
                        } else {
                            OneSignal.User.addTag("hasPlayed", "false");
                        }
                    }
                } else {
                    // hasPlayed tag setter
                    if (guestPlaysLength > 0) {
                        OneSignal.User.addTag("hasPlayed", "true");
                    } else {
                        OneSignal.User.addTag("hasPlayed", "false");
                    }
                }
            }
        } catch (error) {
            console.log(error);
        }
    }, [isInitializedOneSignal, hasUserCreatedAt, userState?.id, guestPlaysLength]);

    // Initialize One Signal
    useEffect(() => {
        void initOneSignal();
    }, [initOneSignal]);

    // Set has played tag for OneSignal
    useEffect(() => {
        void setHasPlayed();
    }, [setHasPlayed]);

    // Set push frequence tag for OneSignal
    useEffect(() => {
        void setPushFrequency();
    }, [setPushFrequency]);

    // Set OneSignal user id tag and alias
    useEffect(() => {
        if (!!userState?.id) {
            OneSignal.User.addAlias("userId", userState?.id);
            OneSignal.User.addTag("userId", userState?.id);
        }
    }, [userState?.id]);

    return <>{children}</>;
};

export default OneSignalWrapper;
